import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";

const DEFAULT_PROMPT =
  "I couldn’t understand your reply.\n\nWhat would you like to do?";
const DEFAULT_HANDOFF_MESSAGE =
  "Agent handoff requested. A team member will follow up shortly.";
const MAX_HANDOFF_MESSAGE_LENGTH = 500;

const ACTION_TYPES = [
  { value: "RepeatCurrentNode", label: "Repeat current question" },
  { value: "StartSelectedFlow", label: "Start selected flow" },
  { value: "TransferToHuman", label: "Transfer to human" },
];

const ATTEMPT_OPTIONS = [1, 2, 3, 4, 5];
const UNMATCHED_TEXT_MODES = [
  {
    value: "DefaultRecoveryOnly",
    label: "Show invalid-message recovery options",
  },
  {
    value: "AiThenRecovery",
    label: "Let AI answer first; use recovery if AI cannot answer",
  },
];

const normalizeUnmatchedTextHandlingMode = value =>
  value === "AiThenRecovery" ? value : "DefaultRecoveryOnly";

const emptyAction = sortOrder => ({
  id: null,
  label: "",
  actionType: "RepeatCurrentNode",
  targetFlowId: null,
  handoffMessage: "",
  sortOrder,
  isActive: true,
});

const normalizeSettings = data => ({
  isEnabled: data?.isEnabled === true,
  promptText:
    typeof data?.promptText === "string" && data.promptText.trim()
      ? data.promptText
      : DEFAULT_PROMPT,
  maxInvalidAttemptsBeforeMenu: ATTEMPT_OPTIONS.includes(
    Number(data?.maxInvalidAttemptsBeforeMenu)
  )
    ? Number(data.maxInvalidAttemptsBeforeMenu)
    : 1,
  unmatchedTextHandlingMode:
    normalizeUnmatchedTextHandlingMode(data?.unmatchedTextHandlingMode),
  afterAiAnswerBehavior:
    data?.afterAiAnswerBehavior || "RepeatCurrentNode",
  aiSimilarityThreshold:
    data?.aiSimilarityThreshold == null ? null : Number(data.aiSimilarityThreshold),
  aiRescueCapability: {
    available: data?.aiRescueCapability?.available === true,
    businessAiEnabled: data?.aiRescueCapability?.businessAiEnabled === true,
    autoReplyPermissionGranted:
      data?.aiRescueCapability?.autoReplyPermissionGranted === true,
    reasonUnavailable:
      data?.aiRescueCapability?.reasonUnavailable ||
      "AI rescue is not available for this business.",
  },
  actions: Array.isArray(data?.actions)
    ? data.actions.map((action, index) => ({
        id: action?.id ?? null,
        label: action?.label ?? "",
        actionType: action?.actionType || "RepeatCurrentNode",
        targetFlowId: action?.targetFlowId ?? null,
        handoffMessage: action?.handoffMessage ?? "",
        sortOrder: Number(action?.sortOrder) || index + 1,
        isActive: action?.isActive !== false,
      }))
    : [],
});

const getApiErrorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.errorMessage ||
  error?.message ||
  "Failed to save recovery settings.";

export default function AutoReplyRecoveryOptionsModal({
  open,
  flowId,
  onClose,
  onSaved,
  onLoaded,
}) {
  const [settings, setSettings] = useState(() => normalizeSettings(null));
  const [targetCandidates, setTargetCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const onLoadedRef = useRef(onLoaded);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = event => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !flowId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setSaveError("");
    setFieldErrors({});

    Promise.all([
      axiosClient.get(`autoreplyflows/${flowId}/recovery-settings`),
      axiosClient.get(`autoreplyflows/${flowId}/recovery-target-candidates`),
    ])
      .then(([settingsResponse, candidatesResponse]) => {
        if (cancelled) return;
        const nextSettings = normalizeSettings(settingsResponse?.data);
        setSettings(nextSettings);
        setTargetCandidates(
          Array.isArray(candidatesResponse?.data) ? candidatesResponse.data : []
        );
        onLoadedRef.current?.(nextSettings);
      })
      .catch(error => {
        if (cancelled) return;
        setLoadError(getApiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, flowId]);

  const candidateMap = useMemo(() => {
    const map = new Map();
    for (const candidate of targetCandidates) {
      const id = candidate?.id || candidate?.flowId;
      if (id) map.set(id, candidate);
    }
    return map;
  }, [targetCandidates]);

  if (!open) return null;

  const updateSettings = patch => {
    setSettings(prev => ({ ...prev, ...patch }));
    setFieldErrors({});
    setSaveError("");
  };

  const updateAction = (index, patch) => {
    setSettings(prev => ({
      ...prev,
      actions: prev.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, ...patch } : action
      ),
    }));
    setFieldErrors({});
    setSaveError("");
  };

  const addAction = () => {
    if (settings.actions.length >= 3) return;
    updateSettings({
      actions: [...settings.actions, emptyAction(settings.actions.length + 1)],
    });
  };

  const removeAction = index => {
    updateSettings({
      actions: settings.actions
        .filter((_, actionIndex) => actionIndex !== index)
        .map((action, actionIndex) => ({
          ...action,
          sortOrder: actionIndex + 1,
        })),
    });
  };

  const validate = () => {
    const errors = {};
    const activeActions = settings.actions.filter(action => action.isActive !== false);

    if (settings.isEnabled) {
      if (!settings.promptText.trim()) {
        errors.promptText = "Recovery message is required.";
      }

      if (activeActions.length === 0) {
        errors.actions = "Add at least one recovery action.";
      }

      if (activeActions.length > 3) {
        errors.actions = "Recovery supports up to 3 actions.";
      }
    }

    activeActions.forEach((action, index) => {
      const prefix = `actions.${index}`;
      if (!String(action.label || "").trim()) {
        errors[`${prefix}.label`] = "Button label is required.";
      } else if (String(action.label).trim().length > 20) {
        errors[`${prefix}.label`] = "Button label must be 20 characters or fewer.";
      }

      if (action.actionType === "StartSelectedFlow") {
        if (!action.targetFlowId) {
          errors[`${prefix}.targetFlowId`] = "Choose a target flow.";
        } else if (!candidateMap.has(action.targetFlowId)) {
          errors[`${prefix}.targetFlowId`] =
            "Selected target flow is no longer eligible. Please choose another flow.";
        }
      }

      if (
        action.actionType === "TransferToHuman" &&
        String(action.handoffMessage || "").trim().length > MAX_HANDOFF_MESSAGE_LENGTH
      ) {
        errors[`${prefix}.handoffMessage`] =
          `Message after handoff must be ${MAX_HANDOFF_MESSAGE_LENGTH} characters or fewer.`;
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      isEnabled: settings.isEnabled,
      promptText: settings.promptText.trim() || DEFAULT_PROMPT,
      maxInvalidAttemptsBeforeMenu:
        Number(settings.maxInvalidAttemptsBeforeMenu) || 1,
      unmatchedTextHandlingMode:
        settings.unmatchedTextHandlingMode || "DefaultRecoveryOnly",
      afterAiAnswerBehavior:
        settings.afterAiAnswerBehavior || "RepeatCurrentNode",
      aiSimilarityThreshold: settings.aiSimilarityThreshold,
      actions: settings.actions.map((action, index) => ({
        id: action.id ?? null,
        label: String(action.label || "").trim(),
        actionType: action.actionType,
        targetFlowId:
          action.actionType === "StartSelectedFlow"
            ? action.targetFlowId || null
            : null,
        handoffMessage:
          action.actionType === "TransferToHuman"
            ? String(action.handoffMessage || "").trim() || null
            : null,
        sortOrder: index + 1,
        isActive: action.isActive !== false,
      })),
    };

    try {
      setSaving(true);
      setSaveError("");
      await axiosClient.put(
        `autoreplyflows/${flowId}/recovery-settings`,
        payload
      );
      toast.success("Recovery options saved.");
      onSaved?.(payload);
      onClose?.();
    } catch (error) {
      setSaveError(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renderTargetFlowSelect = (action, index) => {
    const targetIsIneligible =
      action.targetFlowId && !candidateMap.has(action.targetFlowId);
    const error = fieldErrors[`actions.${index}.targetFlowId`];

    return (
      <div className="mt-2">
        <label
          htmlFor={`auto-reply-recovery-target-flow-${index}`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Target flow
        </label>
        <select
          id={`auto-reply-recovery-target-flow-${index}`}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
            error
              ? "border-rose-300 focus:border-rose-400"
              : "border-slate-200 focus:border-emerald-500"
          }`}
          value={action.targetFlowId || ""}
          onChange={event =>
            updateAction(index, { targetFlowId: event.target.value || null })
          }
        >
          <option value="">Select eligible target flow</option>
          {targetIsIneligible ? (
            <option value={action.targetFlowId}>
              Selected target flow is no longer eligible
            </option>
          ) : null}
          {targetCandidates.map(candidate => {
            const id = candidate?.id || candidate?.flowId;
            return (
              <option key={id} value={id}>
                {candidate?.name || candidate?.flowName || "Untitled flow"}
              </option>
            );
          })}
        </select>
        {targetCandidates.length === 0 ? (
          <p className="mt-1 text-[11px] text-amber-600">
            No eligible target flows found for this WhatsApp number.
          </p>
        ) : null}
        {targetIsIneligible ? (
          <p className="mt-1 text-[11px] text-rose-600">
            Selected target flow is no longer eligible. Please choose another flow.
          </p>
        ) : null}
        {error ? <p className="mt-1 text-[11px] text-rose-600">{error}</p> : null}
      </div>
    );
  };

  const renderHandoffMessageField = (action, index) => {
    const value = String(action.handoffMessage || "");
    const trimmedLength = value.trim().length;
    const error = fieldErrors[`actions.${index}.handoffMessage`];

    return (
      <div className="mt-2">
        <label
          htmlFor={`auto-reply-recovery-handoff-message-${index}`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Message after handoff
        </label>
        <textarea
          id={`auto-reply-recovery-handoff-message-${index}`}
          value={value}
          onChange={event =>
            updateAction(index, { handoffMessage: event.target.value })
          }
          rows={3}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
            error
              ? "border-rose-300 focus:border-rose-400"
              : "border-slate-200 focus:border-emerald-500"
          }`}
          placeholder={DEFAULT_HANDOFF_MESSAGE}
        />
        <div className="mt-1 flex justify-between gap-2 text-[11px]">
          <span className={error ? "text-rose-600" : "text-slate-500"}>
            {error ||
              "This message will be sent to the customer after the chat is transferred to your team."}
          </span>
          <span
            className={
              trimmedLength > MAX_HANDOFF_MESSAGE_LENGTH
                ? "text-rose-600"
                : "text-slate-400"
            }
          >
            {trimmedLength}/{MAX_HANDOFF_MESSAGE_LENGTH}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[1002] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-options-title"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2
              id="recovery-options-title"
              className="text-lg font-bold text-slate-900"
            >
              Recovery Options
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
              This recovery menu appears only when the customer is already inside
              this flow and sends an invalid or unmatched reply. It does not
              replace the Default Entry Flow.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close recovery options"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Loading recovery options…
            </div>
          ) : loadError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {loadError}
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="auto-reply-unmatched-text-mode"
                  className="mb-1 block text-sm font-semibold text-slate-700"
                >
                  When customer sends unmatched text
                </label>
                <select
                  id="auto-reply-unmatched-text-mode"
                  value={settings.unmatchedTextHandlingMode}
                  onChange={event =>
                    updateSettings({
                      unmatchedTextHandlingMode: event.target.value,
                    })
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  {UNMATCHED_TEXT_MODES.map(mode => {
                    const disabled =
                      mode.value === "AiThenRecovery" &&
                      !settings.aiRescueCapability.available;
                    return (
                      <option
                        key={mode.value}
                        value={mode.value}
                        disabled={disabled}
                      >
                        {mode.label}
                        {disabled ? " - Unavailable" : ""}
                      </option>
                    );
                  })}
                </select>

                {settings.unmatchedTextHandlingMode === "AiThenRecovery" ? (
                  <div className="mt-2 space-y-2 rounded-lg border border-sky-100 bg-sky-50 p-3">
                    <p className="text-xs leading-relaxed text-sky-800">
                      Uses approved business knowledge. If AI cannot answer safely,
                      existing recovery options will be shown.
                    </p>
                    <p className="text-xs leading-relaxed text-sky-800">
                      This applies only when the customer is already inside this
                      flow and sends unmatched text. Outside-flow AI replies are
                      managed from AI Agent Settings.
                    </p>
                    <label
                      htmlFor="auto-reply-after-ai-answer"
                      className="block text-xs font-medium text-slate-700"
                    >
                      After AI answer
                    </label>
                    <select
                      id="auto-reply-after-ai-answer"
                      value={settings.afterAiAnswerBehavior}
                      onChange={event =>
                        updateSettings({
                          afterAiAnswerBehavior: event.target.value,
                        })
                      }
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <option value="RepeatCurrentNode">
                        Repeat current flow question/options
                      </option>
                      <option value="KeepPausedWithoutPrompt">
                        Keep flow paused without repeating
                      </option>
                    </select>
                  </div>
                ) : null}

                {!settings.aiRescueCapability.available ? (
                  <p
                    role="alert"
                    className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
                  >
                    AI option unavailable:{" "}
                    {settings.aiRescueCapability.reasonUnavailable}
                  </p>
                ) : null}
              </div>

              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                <label className="flex cursor-pointer items-center justify-between gap-4">
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">
                      Enable recovery menu
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      Show configured buttons after invalid replies inside this flow.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.isEnabled}
                    onChange={event =>
                      updateSettings({ isEnabled: event.target.checked })
                    }
                    className="h-5 w-5 accent-emerald-600"
                  />
                </label>
              </div>

              <div>
                <label
                  htmlFor="auto-reply-recovery-prompt"
                  className="mb-1 block text-sm font-semibold text-slate-700"
                >
                  Recovery message
                </label>
                <textarea
                  id="auto-reply-recovery-prompt"
                  value={settings.promptText}
                  onChange={event =>
                    updateSettings({ promptText: event.target.value })
                  }
                  rows={4}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none ${
                    fieldErrors.promptText
                      ? "border-rose-300 focus:border-rose-400"
                      : "border-slate-200 focus:border-emerald-500"
                  }`}
                />
                {fieldErrors.promptText ? (
                  <p className="mt-1 text-xs text-rose-600">
                    {fieldErrors.promptText}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="auto-reply-recovery-max-attempts"
                  className="mb-1 block text-sm font-semibold text-slate-700"
                >
                  Max invalid attempts before menu
                </label>
                <select
                  id="auto-reply-recovery-max-attempts"
                  value={settings.maxInvalidAttemptsBeforeMenu}
                  onChange={event =>
                    updateSettings({
                      maxInvalidAttemptsBeforeMenu: Number(event.target.value),
                    })
                  }
                  className="w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  {ATTEMPT_OPTIONS.map(value => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  1 means show recovery options on the first invalid reply.
                </p>
              </div>

              <div className="rounded-xl border border-slate-200">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Recovery actions
                    </h3>
                    <p className="text-xs text-slate-500">
                      Button label is customer-facing text. Action type and target
                      flow decide the behavior.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addAction}
                    disabled={settings.actions.length >= 3}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Plus size={14} /> Add action
                  </button>
                </div>

                <div className="space-y-3 p-4">
                  {fieldErrors.actions ? (
                    <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-600">
                      {fieldErrors.actions}
                    </p>
                  ) : null}

                  {settings.actions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                      No recovery actions configured yet.
                    </div>
                  ) : null}

                  {settings.actions.map((action, index) => {
                    const labelError = fieldErrors[`actions.${index}.label`];
                    return (
                      <div
                        key={action.id || `new-${index}`}
                        className="rounded-xl border border-slate-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="grid flex-1 gap-3 md:grid-cols-2">
                            <div>
                              <label
                                htmlFor={`auto-reply-recovery-action-label-${index}`}
                                className="mb-1 block text-xs font-medium text-slate-600"
                              >
                                Button label
                              </label>
                              <input
                                id={`auto-reply-recovery-action-label-${index}`}
                                value={action.label}
                                maxLength={20}
                                onChange={event =>
                                  updateAction(index, {
                                    label: event.target.value,
                                  })
                                }
                                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
                                  labelError
                                    ? "border-rose-300 focus:border-rose-400"
                                    : "border-slate-200 focus:border-emerald-500"
                                }`}
                                placeholder="Customer-facing text"
                              />
                              <div className="mt-1 flex justify-between text-[11px]">
                                <span className="text-rose-600">
                                  {labelError || ""}
                                </span>
                                <span className="text-slate-400">
                                  {String(action.label || "").length}/20
                                </span>
                              </div>
                            </div>

                            <div>
                              <label
                                htmlFor={`auto-reply-recovery-action-type-${index}`}
                                className="mb-1 block text-xs font-medium text-slate-600"
                              >
                                Action type
                              </label>
                              <select
                                id={`auto-reply-recovery-action-type-${index}`}
                                value={action.actionType}
                                onChange={event =>
                                  updateAction(index, {
                                    actionType: event.target.value,
                                    targetFlowId:
                                      event.target.value === "StartSelectedFlow"
                                        ? action.targetFlowId
                                        : null,
                                    handoffMessage:
                                      event.target.value === "TransferToHuman"
                                        ? action.handoffMessage
                                        : "",
                                  })
                                }
                                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                              >
                                {ACTION_TYPES.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeAction(index)}
                            className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            aria-label={`Remove recovery action ${index + 1}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        {action.actionType === "StartSelectedFlow"
                          ? renderTargetFlowSelect(action, index)
                          : null}
                        {action.actionType === "TransferToHuman"
                          ? renderHandoffMessageField(action, index)
                          : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <ShieldAlert size={16} className="mt-0.5 shrink-0 text-slate-400" />
                <p>
                  Customers never see the target-flow list. They only see the
                  button labels configured here.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
          <div className="min-h-[1rem] text-sm text-rose-600">
            {saveError || ""}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || !!loadError || saving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Recovery Options"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
